import React, { useState, useEffect } from "react";
import styled from "styled-components";

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  min-height: 300px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHead = styled.thead`
  background-color: #f8f9fa;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #dee2e6;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px;
  font-weight: 600;
  color: #495057;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};

  &:hover {
    background-color: ${props => props.sortable ? '#e9ecef' : 'transparent'};
  }
`;

const TableCell = styled.td`
  padding: 12px;
  color: #495057;
  vertical-align: middle;
`;

const TableBody = styled.tbody`
  max-height: 250px;
  overflow-y: auto;
`;

const Button = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background-color: #007bff;
    color: white;
    &:hover {
      background-color: #0056b3;
    }
  ` : `
    background-color: transparent;
    color: #007bff;
    border: 1px solid #007bff;
    &:hover {
      background-color: #007bff;
      color: white;
    }
  `}

  &:disabled {
    background-color: #e9ecef;
    color: #6c757d;
    border-color: #e9ecef;
    cursor: not-allowed;
  }
`;

const Text = styled.span`
  font-size: ${props => props.size === 'small' ? '12px' : props.size === 'large' ? '16px' : '14px'};
  font-weight: ${props => props.weight === 'semibold' ? '600' : props.weight === 'bold' ? '700' : '400'};
  color: ${props => props.color || '#495057'};
  display: ${props => props.block ? 'block' : 'inline'};
`;

const ReportNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6c757d;
`;

const SelectedInfo = styled.div`
  padding: 8px 12px;
  background-color: #e7f3ff;
  border-radius: 4px;
  border-left: 3px solid #007bff;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid #dee2e6;
`;

const PaginationInfo = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PaginationButton = styled.button`
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#495057'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f8f9fa'};
  }

  &:disabled {
    background: #e9ecef;
    color: #6c757d;
    cursor: not-allowed;
  }
`;

const ITEMS_PER_PAGE = 10;

const DebriefSelector = ({
  auditReports,
  onSelect,
  selectedDebrief,
  loading
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [debriefData, setDebriefData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Format date helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Aggregate reports by name and combine their data
  const aggregateReportsByName = (reports) => {
    const grouped = {};
    
    reports.forEach(report => {
      const name = report.name || "Unknown Report";
      if (!grouped[name]) {
        grouped[name] = {
          name: name,
          reports: [],
          allResponses: [],
          totalResponseCount: 0,
          latestDate: report.date,
          reportIds: []
        };
      }
      
      grouped[name].reports.push(report);
      grouped[name].reportIds.push(report.id);
      
      // Combine all responses
      if (report.responses && report.responses.length > 0) {
        grouped[name].allResponses.push(...report.responses);
        grouped[name].totalResponseCount += report.responses.length;
      }
      
      // Keep the latest date
      if (new Date(report.date) > new Date(grouped[name].latestDate)) {
        grouped[name].latestDate = report.date;
      }
    });
    
    return Object.values(grouped);
  };

  // Process audit reports data to create table rows
  useEffect(() => {
    if (!auditReports?.response?.length) {
      setDebriefData([]);
      return;
    }

    // Aggregate reports by name
    const aggregatedReports = aggregateReportsByName(auditReports.response);
    
    // Filter out entries with no data and process for table
    const processedData = aggregatedReports
      .filter(group => group.totalResponseCount > 0) // Only show groups with data
      .map(group => ({
        id: group.name, // Use name as ID for grouping
        reportName: (
          <ReportNameCell>
            <Text weight="semibold">{group.name}</Text>
            <Text size="small" color="#6c757d">
              {group.reportIds.length} report{group.reportIds.length > 1 ? 's' : ''} • IDs: {group.reportIds.join(', ')}
            </Text>
          </ReportNameCell>
        ),
        date: formatDate(group.latestDate),
        responses: `${group.totalResponseCount} responses`,
        actions: (
          <Button
            variant={selectedDebrief?.name === group.name ? "primary" : "outline"}
            onClick={() => handleDebriefSelect(group)}
          >
            {selectedDebrief?.name === group.name ? "Selected" : "Select"}
          </Button>
        ),
        // Store raw data for selection and sorting
        rawData: group,
        dateValue: group.latestDate,
        responseCount: group.totalResponseCount
      }))
      .sort((a, b) => new Date(b.dateValue) - new Date(a.dateValue)); // Sort by latest date descending

    setDebriefData(processedData);
  }, [auditReports, selectedDebrief]);

  // Handle search filtering
  useEffect(() => {
    if (searchValue) {
      const filtered = debriefData.filter(row => {
        const reportName = row.rawData.name?.toLowerCase() || "";
        const search = searchValue.toLowerCase();
        return reportName.includes(search);
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(debriefData);
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [debriefData, searchValue]);

  // Handle debrief selection
  const handleDebriefSelect = (debriefGroup) => {
    // Extract useful data for comparison - now includes all aggregated data
    const comparisonData = {
      name: debriefGroup.name,
      reportIds: debriefGroup.reportIds,
      reportCount: debriefGroup.reports.length,
      latestDate: debriefGroup.latestDate,
      allResponses: debriefGroup.allResponses,
      totalResponseCount: debriefGroup.totalResponseCount,
      reports: debriefGroup.reports, // All individual reports
      // Extract survey structure from first report (assuming same structure across reports with same name)
      surveyStructure: debriefGroup.reports[0]?.question,
      surveyType: debriefGroup.reports[0]?.question?.sort_order
    };
    
    // Pass the first report as the main selection for compatibility
    onSelect(debriefGroup.reports[0], comparisonData);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Loading state
  if (loading) {
    return (
      <LoadingState>
        <Text>Loading audit reports...</Text>
      </LoadingState>
    );
  }

  // Empty state
  if (!debriefData.length) {
    return (
      <EmptyState>
        <Text size="large" weight="semibold" color="#6c757d">
          No Audit Reports with Data Found
        </Text>
        <Text color="#6c757d" style={{ marginTop: 8 }}>
          Complete a survey to generate audit reports for comparison
        </Text>
      </EmptyState>
    );
  }

  return (
    <Container>
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search reports..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </SearchContainer>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Report Name</TableHeader>
              <TableHeader sortable>Latest Date</TableHeader>
              <TableHeader sortable>Total Responses</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.reportName}</TableCell>
                <TableCell><Text color="#6c757d">{row.date}</Text></TableCell>
                <TableCell><Text color="#6c757d">{row.responses}</Text></TableCell>
                <TableCell>{row.actions}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of {filteredData.length} reports
          </PaginationInfo>
          <PaginationButtons>
            <PaginationButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <PaginationButton
                key={page}
                active={page === currentPage}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PaginationButton>
            ))}
            <PaginationButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationButtons>
        </PaginationContainer>
      )}
      
      {selectedDebrief && (
        <SelectedInfo>
          <Text size="small" color="#0c5460">
            Selected: {selectedDebrief.name || `Report #${selectedDebrief.id}`}
          </Text>
        </SelectedInfo>
      )}
    </Container>
  );
};

export default DebriefSelector; 